/**
 * Mirrors the cart's POST-discount total (`cart.total_price`), with the promo
 * engine's OWN discounts added back, into the `_promo_spend` cart attribute,
 * where the checkout discount Function can read it (SHOP-1354).
 *
 * Why: a Shopify discount Function's cost input is pre-discount by design, so
 * at checkout it cannot see discounts applied by other systems — Okendo points,
 * other discount apps, native codes. This bridges the shopper's *true spend* in
 * so the engine can gate spend-thresholds (free gift, free shipping, tiered
 * $-off/%-off) on it.
 *
 * "True spend" = post-EXTERNAL-discount, pre-tender, pre-engine. Promotions
 * and points-as-discount reduce `cart.total_price`; gift card / store credit /
 * PayPal / Afterpay are payment tenders applied later and never touch it — so
 * they correctly still count toward earning.
 *
 * The engine's own Function discounts must NOT reduce this value — the engine
 * gates its spend thresholds on it, so including its own discount is circular:
 * a 20%-off-over-$100 campaign on a $108 cart would drop spend to $86.40,
 * un-qualify itself, restore the total, re-qualify, and oscillate forever
 * (an infinite /cart/update.js + /evaluate loop, reproduced on a 2-item cart).
 * Engine discounts are identified by the trailing `(#XXXX)` campaign
 * short-code marker promo-api stamps on automatic discount messages, and
 * added back onto `total_price`. Code-gated engine discounts carry no marker
 * (known residual — see docs/promo-integration.md).
 *
 * KEEP THE FORMULA IN SYNC with the promo block's canonical, unit-tested copy:
 * apps/shopify-app/extensions/promo-block/assets/cart-discounts.js
 * (#promoSpendCents / #engineDiscountCents) — consumed by promo-cart.js's own
 * writer. Two writers with different formulas would fight over the attribute.
 *
 * Runs on load and on every `cart:update` (Horizon's `ThemeEvents.cartUpdate`,
 * dispatched on `document` after cart mutations — covers line-qty changes and
 * discount codes entered on the cart page). The promo-block picker's own raw
 * cart mutations don't dispatch `cart:update`, so its refresh path writes this
 * attribute itself (see `promo-cart.js` `_applyState` → `writePromoSpendAttribute`).
 *
 * Notes:
 *  - `/cart/update.js` is public, so this attribute is forgeable. The checkout
 *    engine clamps it to at most the merchandise total, so a forged value can
 *    never earn MORE than the pre-discount total already would.
 *  - Theme JS can't run on Shopify's hosted checkout, so a code entered for the
 *    first time ON the checkout page (skipping the cart) won't be captured — a
 *    documented residual (fails in the customer-friendly direction).
 */
(function () {
  if (window.Shopify?.designMode) return;

  let syncing = false;

  // Sum of the promo engine's own Function discounts on this cart, identified
  // by the end-anchored `(#XXXX)` short-code marker on the application title.
  // Mirror of cart-discounts.js#engineDiscountCents (see header).
  function engineDiscountCents(cart) {
    const marker = /\(#[A-Z0-9]{4}\)\s*$/;
    const marked = (app) =>
      !!app && typeof app.title === "string" && marker.test(app.title);
    let cents = 0;
    for (const app of cart.cart_level_discount_applications ?? []) {
      if (marked(app) && typeof app.total_allocated_amount === "number") {
        cents += app.total_allocated_amount;
      }
    }
    for (const line of cart.items ?? []) {
      const isGiftLine = !!line?.properties?._promo_gwp;
      for (const alloc of line?.line_level_discount_allocations ?? []) {
        if (
          (isGiftLine || marked(alloc?.discount_application)) &&
          typeof alloc.amount === "number"
        ) {
          cents += alloc.amount;
        }
      }
    }
    return cents;
  }

  async function syncPromoSpendAttribute() {
    // Coalesce overlapping runs (load + a burst of cart:update events).
    if (syncing) return;
    syncing = true;
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      if (!res.ok) return;
      const cart = await res.json();
      if (typeof cart.total_price !== "number") return;
      const desired = String(cart.total_price + engineDiscountCents(cart));
      const current = cart.attributes?._promo_spend ?? "";
      // Idempotent: only write when it actually changed, so this never loops
      // and adds no request when the cart is unchanged.
      if (current === desired) return;
      // SHOP-1408: cross-writer write memo, shared on `window` with the
      // promo block's writer (promo-cart.js#writePromoSpendAttribute — keep
      // in sync). On a Horizon mutation both writers act on cart snapshots
      // read BEFORE either write lands — the block's writer may run seconds
      // later (it waits for its /evaluate response) — so both "only write
      // when changed" checks pass and the same value was POSTed twice. Skip
      // when that value is already in flight or was successfully written
      // moments ago (TTL-bounded: only these writers touch the attribute,
      // but a stale memo must never outlive a real divergence for long).
      const memo = window.__promoSpendWrite;
      if (
        memo &&
        memo.value === desired &&
        (memo.inFlight || Date.now() - memo.at < 15000)
      ) {
        return;
      }
      const claim = { value: desired, inFlight: true, at: 0 };
      window.__promoSpendWrite = claim;
      let writeRes;
      try {
        writeRes = await fetch("/cart/update.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: { _promo_spend: desired } }),
        });
      } catch (err) {
        if (window.__promoSpendWrite === claim) window.__promoSpendWrite = null;
        throw err;
      }
      claim.inFlight = false;
      if (writeRes.ok) {
        claim.at = Date.now();
      } else if (window.__promoSpendWrite === claim) {
        window.__promoSpendWrite = null;
      }
      // SHOP-1404: the write can flip an engine spend-threshold discount, and
      // Shopify's line-key hash includes discount allocations — a flip rotates
      // EVERY cart line key, stranding stale [data-key]s in the rendered cart
      // (Horizon's key-addressed /cart/change then fails with "no valid id or
      // line parameter"). If the response shows rotated keys, dispatch
      // cart:update with no sections payload so cart-items-component
      // re-fetches fresh section HTML. One-shot: the re-triggered sync (this
      // script listens for cart:update) finds the attribute unchanged and
      // writes nothing. Mirror of promo-cart.js#writePromoSpendAttribute —
      // keep in sync with cart-discounts.js#cartLineKeysChanged.
      if (!writeRes.ok) return;
      const updated = await writeRes.json().catch(() => null);
      if (!updated || !Array.isArray(updated.items)) return;
      const keysOf = (c) =>
        c.items
          .map((i) => i?.key)
          .filter((k) => typeof k === "string")
          .sort();
      const beforeKeys = keysOf(cart);
      const afterKeys = keysOf(updated);
      const rotated =
        beforeKeys.length !== afterKeys.length ||
        beforeKeys.some((k, i) => k !== afterKeys[i]);
      if (rotated) {
        document.dispatchEvent(
          new CustomEvent("cart:update", {
            bubbles: true,
            detail: {
              resource: updated,
              sourceId: "promo-spend-attribute",
              // itemCount: cart-icon.js renders the bubble from
              // detail.data.itemCount ?? 0 — omitting it would zero the
              // bubble on this dispatch.
              data: {
                source: "promo-spend-attribute",
                itemCount: updated.item_count,
              },
            },
          }),
        );
      }
    } catch {
      // Best-effort; the next cart:update or page load retries.
    } finally {
      syncing = false;
    }
  }

  syncPromoSpendAttribute();
  // Literal "cart:update" (== ThemeEvents.cartUpdate in assets/events.js) —
  // kept import-free like promo-authed.js so a module-resolution hiccup can't
  // disable it. Keep in sync with assets/events.js.
  document.addEventListener("cart:update", syncPromoSpendAttribute);
})();
